import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Checkbox,
  Input,
} from '@staff-app/ui';
import { Sparkles, ExternalLink, Lock, ShieldAlert } from 'lucide-react';
import type { DeviceOrderTask } from '@staff-app/shared-types';
import { isDraftStatus, isPaidStatus } from '../lib/status';
import {
  useCloseDeviceOrderTask,
  useDeviceOrderCouponQuote,
  useMintDeviceOrderCoupon,
  useOrderDeviceCanBypassPaymentGate,
} from '../hooks/useOrderDeviceTasks';
import { useIsOrderRoot } from './DismissOrderButton';

const money = (n: number) => `$${n.toFixed(2)}`;

/** One label/value line of the calculation. */
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="truncate">{label}</dt>
      <dd className="shrink-0 font-mono">{value}</dd>
    </div>
  );
}

/**
 * Coupon actions for a task:
 *   - "Mint coupon" — one-click: creates the Shopify discount code in-app
 *     (fixed $ = this device's amount, editable; scoped to that product), attributed
 *     to the acting staffer. This is the "kill the shared Shopify login" path.
 *   - "Enter code" — manual fallback: paste an externally-created code.
 * Both close the task + drop it from the active queue.
 */
export function MintCouponButton({
  task,
  productIndex,
  deviceName,
  onMinted,
}: {
  task: DeviceOrderTask;
  /** Which device line this coupon is for (each device gets its own code). */
  productIndex?: number;
  deviceName?: string;
  /**
   * Report a successful mint UP to the page, which owns the confirmation dialog. It cannot
   * live here: a successful mint gives the task a coupon, so the table re-renders this row as
   * "Issued" and unmounts this component — any dialog state held here dies with it.
   */
  onMinted?: (c: { code: string; amount: number | null; deviceName?: string | null; customer: string }) => void;
}) {
  // Drafts can never be issued a coupon — the invoice is not finalised, so the amounts can
  // still change and it may never become an order. The service refuses it as well; this exists
  // so the operator gets told rather than filling in a form that will fail.
  // Canonicalised, not a raw compare: a backfilled row spells these differently (see
  // lib/status.ts), and treating its PAID as "not paid" would gate an order that is settled.
  const isDraft = isDraftStatus(task.status);
  const [draftBlockOpen, setDraftBlockOpen] = useState(false);
  const [mintOpen, setMintOpen] = useState(false);
  const [enterOpen, setEnterOpen] = useState(false);
  // The coupon is priced from the Mongo CATALOG, not the invoice (an order invoiced at an
  // old/negotiated price still needs a coupon that covers today's cart). Fetch that quote
  // when the dialog opens so the field shows exactly what the mint will use.
  // Ship-to region decides the freight rate (US $7.90/unit vs international $30/unit).
  // Stripe often has no ship-to country, so the server's derived default must be
  // correctable here — undefined means "use the server's derivation".
  const [region, setRegion] = useState<string | undefined>(undefined);
  const { data: quoteData, loading: quoting, error: quoteError } = useDeviceOrderCouponQuote(
    task._id,
    productIndex,
    mintOpen && !isDraft,
    region,
  );
  const quote = quoteData?.deviceOrderCouponQuote;
  const [amount, setAmount] = useState<string>('');
  const [edited, setEdited] = useState(false);
  // Adopt the quote once it lands, unless the operator has typed their own figure.
  useEffect(() => {
    if (!edited && quote) setAmount(String(quote.totalAmount));
  }, [quote, edited]);
  const [code, setCode] = useState('');

  // THE PAYMENT GATE. A coupon must not be issued before the invoice is paid; only staff on
  // the ORDER_DEVICE_GATE_BYPASS_EMAILS allowlist may approve an exception, with a reason.
  // This is UX only — the server refuses either way, so a stale answer here is never a hole.
  const isPaid = isPaidStatus(task.status);
  // A draft is refused outright, by its own rule, so it must not also ask for the gate's
  // approval query — that would offer an override for something no approval can unlock.
  const gated = !isPaid && !isDraft;
  const { canBypass, loading: gateLoading } = useOrderDeviceCanBypassPaymentGate(gated);
  // Until the capability answer lands, commit to NEITHER label. Defaulting to blocked flashed
  // "Payment required" at an approver for a frame; defaulting to allowed would offer an
  // override the server may refuse. So: briefly inert, then correct.
  const gateUnknown = gated && gateLoading;
  const blocked = gated && !gateLoading && !canBypass;
  const [gateBlockOpen, setGateBlockOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const needsReason = gated && !overrideReason.trim();

  const [mint, { loading: minting, error: mintError }] = useMintDeviceOrderCoupon();
  const [close, { loading: closing, error: closeError }] = useCloseDeviceOrderTask();
  // Root may record a code Shopify says is fully redeemed — one given to this customer by hand
  // before the app, which they have already used. The server re-checks the role.
  const isRoot = useIsOrderRoot();
  const [acceptRedeemed, setAcceptRedeemed] = useState(false);

  // Same precedence as the queue's Customer column — the owner is who staff recognise.
  const customer = task.ownerEmail || task.customerEmail || task.stripeCustomerId || task._id;
  const who = deviceName ? `${deviceName} — ${customer}` : customer;

  const submitMint = async () => {
    const amt = parseFloat(amount);
    if (!(amt > 0)) return;
    try {
      const res = await mint({
        variables: {
          invoiceId: task._id,
          productIndex,
          amount: amt,
          region: quote?.region,
          // Only sent for an unpaid invoice — the server ignores it on a paid one.
          overrideReason: gated ? overrideReason.trim() : undefined,
        },
      });
      const coupons = res.data?.mintDeviceOrderCoupon?.coupons ?? [];
      const created = coupons.length ? coupons[coupons.length - 1].code : null;
      // Close the form and hand the result to the page. The sentinel covers a successful mint
      // whose response carried no code — the coupon exists either way, so the operator has to
      // be told rather than left with a form that just vanished.
      setMintOpen(false);
      onMinted?.({
        code: created ?? '(created — no code returned; check Shopify)',
        amount: amt,
        deviceName,
        customer,
      });
    } catch {
      // Error is surfaced to the user via `mintError`; keep the dialog open.
    }
  };

  const closeMint = () => {
    setMintOpen(false);
  };

  const submitEnter = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    try {
      await close({
        variables: {
          invoiceId: task._id,
          couponCode: trimmed,
          productIndex,
          overrideReason: gated ? overrideReason.trim() : undefined,
          acceptRedeemed: isRoot && acceptRedeemed ? true : undefined,
        },
      });
      setEnterOpen(false);
      setCode('');
      setAcceptRedeemed(false);
    } catch {
      // Error surfaced via `closeError`; keep the dialog open.
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        className="gap-1"
        onClick={() =>
          isDraft
            ? setDraftBlockOpen(true)
            : blocked
              ? setGateBlockOpen(true)
              : setMintOpen(true)
        }
        disabled={gateUnknown}
        data-testid="order-mint"
      >
        {isDraft || blocked ? <Lock className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
        {/* A draft can never be minted, by anyone — so the button says so instead of offering
            "Mint coupon" and only revealing the refusal after a click. Same treatment as the
            payment gate's "Payment required": if a state is blocked, the label is the place to
            say it. Still clickable, so the dialog can explain why. isDraft wins because that
            rule is absolute — no approval unlocks it. */}
        {isDraft
          ? "Draft — can't mint"
          : gateUnknown
            ? 'Checking…'
            : blocked
              ? 'Payment required'
              : gated
                ? 'Approve & mint'
                : 'Mint coupon'}
      </Button>

      <Button
        size="sm"
        variant="ghost"
        onClick={() =>
          isDraft
            ? setDraftBlockOpen(true)
            : blocked
              ? setGateBlockOpen(true)
              : setEnterOpen(true)
        }
        disabled={gateUnknown}
        data-testid="order-enter-code"
      >
        {'Enter code'}
      </Button>

      {/* Draft — refuse, and say why. Covers BOTH issuance paths, since the invariant is
          "a draft carries no coupon", not "a draft cannot be minted from". */}
      <Dialog open={draftBlockOpen} onOpenChange={setDraftBlockOpen}>
        <DialogContent data-testid="order-draft-block">
          <DialogHeader>
            <DialogTitle>{'Draft invoices cannot mint coupons'}</DialogTitle>
            <DialogDescription>
              {`${task.invoiceId || task._id} is still a DRAFT in Stripe, so nothing is owed yet and its amounts can still change. Finalise the invoice first — the coupon is priced against a real order.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDraftBlockOpen(false)} data-testid="order-draft-block-ok">
              {'Got it'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unpaid + not an approver — refuse, and say who CAN approve. Same idiom as the draft
          refusal above: an explanation beats a dead button the operator cannot interpret.
          Covers both issuance paths, because the rule is "no code for an unpaid invoice", not
          "no minting from an unpaid invoice". */}
      <Dialog open={gateBlockOpen} onOpenChange={setGateBlockOpen}>
        <DialogContent data-testid="order-gate-block">
          <DialogHeader>
            <DialogTitle>{'This invoice is not paid'}</DialogTitle>
            <DialogDescription>
              {`${task.invoiceId || task._id} is ${task.status || 'not paid'}, so a coupon cannot be issued for it. Issuing before payment needs management approval — ask Paul, Shawna or Henry to do it from this page, and they will be recorded as having approved it.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setGateBlockOpen(false)} data-testid="order-gate-block-ok">
              {'Got it'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mint — the FORM only. On success this closes and the confirmation below takes over. */}
      <Dialog open={mintOpen} onOpenChange={(o) => (o ? setMintOpen(true) : closeMint())}>
        <DialogContent>
          <>
              <DialogHeader>
                <DialogTitle>{'Mint Shopify coupon'}</DialogTitle>
                <DialogDescription>
                  {`Creates a single-use Shopify discount code (fixed $ off ${deviceName ? 'this product' : 'the order'}) for ${who} and attributes it to you. Confirm the amount — defaults to this device's current catalog price plus shipping.`}
                </DialogDescription>
              </DialogHeader>
              {(task.invoiceId || '').startsWith('in_') ? (
                <div className="text-sm">
                  <span className="text-muted-foreground">{'Invoice: '}</span>
                  <a
                    href={`https://dashboard.stripe.com/invoices/${task.invoiceId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                    title="Open invoice in Stripe"
                  >
                    {task.invoiceId}
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                </div>
              ) : null}
              {gated ? (
                <div
                  className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3"
                  data-testid="order-gate-override"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-500">
                    <ShieldAlert className="h-4 w-4 shrink-0" />
                    {`This invoice is not paid (${task.status || 'unknown'})`}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {`You are approving a coupon BEFORE payment. If it is still unpaid ${
                      task.netTermsDays != null && task.netTermsDays >= 60
                        ? 'it will be tracked on the Unpaid coupons tab'
                        : "in 60 days, support is asked to chase them at day 50 and the account's screens are switched off at day 60"
                    }. Your name and reason are recorded on the order.`}
                  </p>
                  <Input
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="Reason — e.g. signed PO on file, district pays net 45"
                    data-testid="order-gate-reason"
                  />
                </div>
              ) : null}
              <label className="text-sm font-medium">{'Discount amount (USD)'}</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => {
                  setEdited(true);
                  setAmount(e.target.value);
                }}
                placeholder={quoting ? 'Pricing from the catalog…' : 'e.g. 6473.40'}
                data-testid="order-mint-amount"
              />
              {quote ? (
                <div className="flex items-center gap-2 text-xs" data-testid="order-mint-region">
                  <span className="text-muted-foreground">{'Ship to:'}</span>
                  {(['us', 'international'] as const).map((r) => (
                    <Button
                      key={r}
                      size="sm"
                      variant={quote.region === r ? 'secondary' : 'ghost'}
                      onClick={() => {
                        setRegion(r);
                        setEdited(false); // re-adopt the re-quoted amount
                      }}
                    >
                      {r === 'us' ? 'US' : 'International'}
                    </Button>
                  ))}
                  {quote.customerCountry ? (
                    <span className="text-muted-foreground">{`(invoice country: ${quote.customerCountry})`}</span>
                  ) : (
                    <span className="text-muted-foreground">{'(no country on the invoice — confirm)'}</span>
                  )}
                </div>
              ) : null}
              {quote ? (
                <div className="space-y-2 text-xs text-muted-foreground" data-testid="order-mint-quote">
                  {/* Itemised so an operator can check the arithmetic without a calculator —
                      this is the number a real discount code gets created for. */}
                  <div className="rounded-md border bg-muted/30 p-3">
                    <div className="mb-2 font-medium text-foreground">{'How this amount is calculated'}</div>
                    <dl className="space-y-1">
                      <Row
                        label={`Devices — ${quote.quantity} × ${money(quote.unitPrice)}`}
                        value={money(quote.deviceAmount)}
                      />
                      {quote.shippingAmount > 0 ? (
                        <Row
                          label={
                            quote.shippingUnitPrice != null
                              ? `Configuration & Delivery — ${quote.quantity} × ${money(quote.shippingUnitPrice)}`
                              : 'Configuration & Delivery'
                          }
                          value={money(quote.shippingAmount)}
                        />
                      ) : (
                        <Row label="Configuration & Delivery" value="—" />
                      )}
                      <div className="mt-1 flex justify-between border-t pt-1 font-semibold text-foreground">
                        <dt>{'Coupon total'}</dt>
                        <dd data-testid="order-mint-total">{money(quote.totalAmount)}</dd>
                      </div>
                    </dl>
                    <p className="mt-2 text-[11px]">
                      {`Prices come from the device catalog (${quote.sku}), shipping at the ${
                        quote.region === 'international' ? 'international' : 'US'
                      } rate.`}
                    </p>
                  </div>
                  {quote.invoiceCurrency &&
                  quote.invoiceCurrency.toLowerCase() !== quote.currency.toLowerCase() ? (
                    <p className="text-amber-600 dark:text-amber-500">
                      {`Invoice was billed in ${quote.invoiceCurrency.toUpperCase()}, but the store sells in ${quote.currency.toUpperCase()} — the coupon is priced in ${quote.currency.toUpperCase()}. Confirm the amount.`}
                    </p>
                  ) : null}
                  {quote.invoicedAmount != null &&
                  Math.abs(quote.invoicedAmount - quote.totalAmount) >= 0.01 ? (
                    <p className="text-amber-600 dark:text-amber-500">
                      {`The invoice billed ${'$'}${quote.invoicedAmount.toFixed(2)} — the coupon follows the CURRENT catalog price so it covers the customer's order.`}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {quoteError ? (
                <p className="text-sm text-destructive">{quoteError.message}</p>
              ) : null}
              {mintError ? <p className="text-sm text-destructive">{mintError.message}</p> : null}
              <DialogFooter>
                <Button variant="ghost" onClick={closeMint}>{'Cancel'}</Button>
                <Button
                  onClick={submitMint}
                  disabled={minting || quoting || !(parseFloat(amount) > 0) || needsReason}
                >
                  {minting ? 'Minting…' : gated ? 'Approve & mint' : 'Mint coupon'}
                </Button>
              </DialogFooter>
            </>
        </DialogContent>
      </Dialog>

      {/* Enter code — manual fallback */}
      <Dialog open={enterOpen} onOpenChange={setEnterOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{'Close order — enter coupon code'}</DialogTitle>
            <DialogDescription>
              {`Paste a Shopify discount code already issued for ${who}. Attributes it to you and removes the order from the active queue.`}
            </DialogDescription>
          </DialogHeader>
          {gated ? (
            <div
              className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3"
              data-testid="order-gate-override"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-500">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                {`This invoice is not paid (${task.status || 'unknown'})`}
              </div>
              <p className="text-xs text-muted-foreground">
                {`You are approving a coupon BEFORE payment. If it is still unpaid ${
                  task.netTermsDays != null && task.netTermsDays >= 60
                    ? 'it will be tracked on the Unpaid coupons tab'
                    : "in 60 days, support is asked to chase them at day 50 and the account's screens are switched off at day 60"
                }. Your name and reason are recorded on the order.`}
              </p>
              <Input
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Reason — e.g. signed PO on file, district pays net 45"
                data-testid="order-gate-reason"
              />
            </div>
          ) : null}
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. HCQ5BTABPP47"
            data-testid="order-coupon-input"
          />
          {isRoot ? (
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={acceptRedeemed}
                onCheckedChange={(v) => setAcceptRedeemed(v === true)}
                className="mt-0.5"
                data-testid="order-accept-redeemed"
              />
              <span>
                {'This code was already given to this customer and has been used — record it even if '}
                {'Shopify reports it fully redeemed. (Root only; noted on the order.)'}
              </span>
            </label>
          ) : null}
          {closeError ? <p className="text-sm text-destructive">{closeError.message}</p> : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEnterOpen(false)}>{'Cancel'}</Button>
            <Button onClick={submitEnter} disabled={closing || !code.trim() || needsReason}>
              {closing ? 'Saving…' : 'Save & close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
